<template>
  <div :class="containerClass">
    <page-header :title="title" infoText="Manage your plans." />
    <div class="page-section">
      <page-separator title="" />
      <div v-if="isLoading">
        <div class="card mb-4">
          <div class="card-header">
            <b-skeleton animation="wave" width="30%"></b-skeleton>
          </div>

          <b-skeleton-table :rows="3" :columns="4"></b-skeleton-table>
        </div>
      </div>

      <div v-if="!plans.length && !isLoading">
        <div class="d-flex justify-content-center">
          <div class="card card-block card-stretch card-height col-md-12">
            <div class="card-body text-center">
              <i class="fas fa-exclamation-circle fa-lg text-muted mt-4 mb-4" style="font-size: 3rem" />
              <h4>No Plans Found</h4>

              <p class="text-muted">No payment plans found.</p>
            </div>
          </div>
        </div>
      </div>

      <div v-else v-for="plan in plans" :key="plan.class_id" class="mb-4">
        <div class="card schedule-card mb-0">
          <div class="card-body">
            <div class="schedule-header">
              <div>
                <div class="text-muted schedule-eyebrow">Class #{{ plan.class_id }} &middot; {{ plan.program_name }}</div>
                <h5 class="mb-0 mt-1">Payment schedule</h5>
              </div>
              <div class="schedule-summary">
                <div class="text-muted schedule-eyebrow">Paid so far</div>
                <div class="schedule-paid-amount">
                  <strong class="text-success">${{ $n(paidAmount(plan)) }}</strong>
                  <span class="text-muted"> / ${{ $n(totalAmount(plan)) }}</span>
                </div>
              </div>
            </div>

            <div class="text-muted schedule-eyebrow mt-2 mb-3">
              {{ paidCount(plan) }} of {{ orderedInstallments(plan).length }} installments paid
            </div>

            <div class="schedule-timeline">
              <div
                v-for="(item, idx) in orderedInstallments(plan)"
                :key="item.id"
                class="schedule-item"
                :class="{ 'schedule-item-last': idx === orderedInstallments(plan).length - 1 }"
              >
                <div class="schedule-marker">
                  <div
                    class="schedule-dot"
                    :class="{
                      'schedule-dot-paid': item.status_paid,
                      'schedule-dot-next': isNextInstallment(item, plan),
                      'schedule-dot-cancelled': isCancelled(item),
                    }"
                  >
                    <md-icon v-if="item.status_paid" class="schedule-dot-icon">check</md-icon>
                  </div>
                  <div v-if="idx !== orderedInstallments(plan).length - 1" class="schedule-line"></div>
                </div>

                <router-link
                  class="schedule-content"
                  :class="{ 'schedule-content-next': isNextInstallment(item, plan) }"
                  :to="isCancelled(item) && !item.status_paid ? '' : { name: 'payment-plan-details', params: { id: item.id } }"
                >
                  <div
                    v-if="isNextInstallment(item, plan)"
                    class="schedule-next-label text-primary"
                  >
                    NEXT PAYMENT
                  </div>
                  <div class="schedule-row">
                    <div class="schedule-amount-group">
                      <div class="schedule-amount">${{ $n(item.amount) }}</div>
                      <div class="text-muted schedule-due">
                        {{ isNextInstallment(item, plan) ? 'due' : 'Due' }} {{ formatInstallmentDate(item.date) }}
                      </div>
                    </div>

                    <div class="schedule-action">
                      <b-btn
                        v-if="isNextInstallment(item, plan)"
                        variant="primary"
                        size="sm"
                        @click.stop="$router.push({ name: 'payment-plan-details', params: { id: item.id } })"
                      >
                        Pay now
                      </b-btn>
                      <span v-else-if="item.status_paid" class="text-success schedule-paid-label">
                        Paid {{ formatFullDate(item.date_paid) }}
                      </span>
                      <span v-else-if="isCancelled(item)" class="text-danger">
                        <strong>Cancelled</strong>
                      </span>
                      <span v-else-if="isOverdue(item)" class="text-danger">Overdue</span>
                      <span v-else class="text-primary">Pay early</span>
                    </div>
                  </div>
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { get } from 'lodash';
import PageHeader from '@/components/Ui/PageHeader.vue';
import Page from '@/components/Page.vue';
import { mapActions, mapGetters } from 'vuex';
import { formatDateTime, formatFullDate, formatInstallmentDate, planDateSortKey, isInstallmentOverdue } from '@/common/utils';
import { STUDENT_PAYMENT_TYPES } from '@/common/constants';
import PageSeparator from '@/components/Ui/PageSeparator.vue';

export default {
  components: { PageHeader, PageSeparator },
  extends: Page,

  name: 'PlansList',

  data() {
    return {
      title: 'Manage Plans',
      windowWidth: window.innerWidth,
      isLoading: false,
      plans: [],
      searchTerm: '',
      STUDENT_PAYMENT_TYPES,
    };
  },

  computed: {
    ...mapGetters('auth', ['getLoggedInUser']),
    ...mapGetters('school', ['getCurrentSchool']),

    isMobileScreen() {
      return this.windowWidth <= 776;
    },
  },

  methods: {
    ...mapActions('payments', ['getHEPaymentPlans']),
    formatDateTime,
    formatFullDate,
    formatInstallmentDate,
    get,

    isCancelled(item) {
      return item.status === STUDENT_PAYMENT_TYPES.CANCELLED;
    },

    isOverdue(item) {
      return !item.status_paid && !this.isCancelled(item) && isInstallmentOverdue(item.date);
    },

    orderedInstallments(plan) {
      return [...plan.student_payment_plan].sort((a, b) => planDateSortKey(a.date).localeCompare(planDateSortKey(b.date)));
    },

    paidAmount(plan) {
      return this.orderedInstallments(plan).reduce((sum, item) => (item.status_paid ? sum + Number(item.amount) : sum), 0);
    },

    totalAmount(plan) {
      return this.orderedInstallments(plan).reduce((sum, item) => sum + Number(item.amount), 0);
    },

    paidCount(plan) {
      return this.orderedInstallments(plan).filter((item) => item.status_paid).length;
    },

    nextInstallment(plan) {
      return this.orderedInstallments(plan).find((item) => !item.status_paid && !this.isCancelled(item));
    },

    isNextInstallment(item, plan) {
      const next = this.nextInstallment(plan);
      return !!next && next.id === item.id;
    },

    handleResize() {
      this.windowWidth = window.innerWidth;
    },

    async fetchHEPaymentPlans() {
      this.isLoading = true;
      try {
        const res = await this.getHEPaymentPlans({});

        this.plans = res.data;
      } catch (e) {
        // USE ERROR
      }
      this.isLoading = false;
    },
  },

  async mounted() {
    this.fetchHEPaymentPlans();
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
  },
};
</script>

<style scoped>
.schedule-eyebrow {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.schedule-header {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
}

.schedule-summary {
  text-align: right;
  margin-left: auto;
}

.schedule-paid-amount {
  font-size: 1.05rem;
}

.schedule-timeline {
  margin-top: 8px;
}

.schedule-item {
  display: flex;
  padding-bottom: 20px;
}

.schedule-item-last {
  padding-bottom: 0;
}

.schedule-marker {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 24px;
  margin-right: 16px;
  flex-shrink: 0;
}

.schedule-dot {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 2px solid #d9dee6;
  background-color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.schedule-dot-paid {
  background-color: #77c13a;
  border-color: #77c13a;
}

.schedule-dot-icon {
  color: #fff;
  font-size: 15px !important;
}

.schedule-dot-next {
  border-color: #3b86f5;
}

.schedule-dot-cancelled {
  border-color: #ed0b4c;
}

.schedule-line {
  flex: 1;
  width: 2px;
  background-color: #e3e7ee;
  margin-top: 2px;
}

.schedule-content {
  flex: 1;
  color: inherit;
  text-decoration: none;
  display: block;
}

.schedule-content:hover {
  color: inherit;
  text-decoration: none;
}

.schedule-content-next {
  background-color: rgba(59, 134, 245, 0.08);
  border: 1px solid rgba(59, 134, 245, 0.35);
  border-radius: 8px;
  padding: 12px 16px;
  margin-top: -4px;
}

.schedule-next-label {
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 4px;
}

.schedule-row {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
}

.schedule-amount {
  font-weight: 600;
  font-size: 1rem;
}

.schedule-due {
  font-size: 0.85rem;
}

.schedule-paid-label {
  font-weight: 600;
  font-size: 0.9rem;
}

.schedule-action {
  display: flex;
  align-items: center;
}
</style>
